
import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import PropTypes from 'prop-types';

import _ from 'underscore';
import { getScaChallengeStyles } from '@bingads-webui-campaign-react/common-styles';
import { securityAction } from '@bingads-webui-clientcenter/pidl-service-smart';
import { ElementHelper } from '@bingads-webui-cc-react/helper';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import IWindow from '@bingads-webui-clientcenter/window-interface';
import { PidlUtil } from '../utils/pidl-util';
import { PropTypesGenericPidl, PropTypesDefaultGenericPidl } from '../shapes';
import { ScaChallengerPidlForm } from '../pidl-forms/sca-challenger-pidl-form';
import type {
  I18n,
  PaymentSession,
  PidlError,
  PidlEventName,
  PidlEventParameters,
  PidlInstance,
  ScaChallengerProps,
  ScaChallengerState,
} from '../types';


const ScaChallengeStyles: React.FC = () => {
  useDefaultStyles((_components, { palette = {} } = {}) => getScaChallengeStyles({ palette }).global);
  return null;
};

const LoadingMessage = ({
  i18n,
}: { i18n: I18n }) => {
  const [loadingError, setLoadingError] = useState(false);
  setTimeout(() => {
    setLoadingError(true);
  }, 30000);

  return (
    <React.Fragment>
      <title>{i18n.getString(_TL_('Authenticate your card'))}</title>
      <p>{loadingError ? i18n.getString(_TL_('It took longer than expected to load the response. Please retry or choose a different payment method.'))
        : i18n.getString(_TL_('Loading...'))}
      </p>
    </React.Fragment>
  );
};

export class ScaChallenger extends React.Component<ScaChallengerProps, ScaChallengerState> {
  // Some parameters are defined by Enums from the PIDL contract
  // Reference: https://osgwiki.com/wiki/Pidlsdk#PaymentSession_createPaymentSession_.28PaymentSessionData.29
  static propTypes = {
    ...PropTypesGenericPidl,
    piData: PropTypes.shape({
      CtpPiId: PropTypes.number.isRequired,
      MtPiId: PropTypes.number, // not required because add-pi flow doesn't have mtpiid yet
      PiType: PropTypes.string.isRequired,
    }).isRequired,
    purchaseContext: PropTypes.shape({
      amount: PropTypes.number.isRequired,
      currency: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
      challengeScenario: PropTypes.string.isRequired, // Pidl Enum
    }).isRequired,
    challengeWindowSize: PropTypes.string, // Pidl Enum
    eventHandlers: PropTypes.shape({
      handleTerminatingError: PropTypes.func.isRequired, // Triggered when an error occurs which terminates the flow
      handleScaChallengeSuccess: PropTypes.func.isRequired, // Triggered when a user successfully performs the SCA challenge
      handleScaChallengeFailure: PropTypes.func.isRequired, // Triggered when the user does not perform the SCA challenge successfully.
      handleGoHome: PropTypes.func.isRequired, // Triggered when the user cancels the form of closes the lightbox
      handleProcessingStart: PropTypes.func, // Triggered right before the Pidl form is rendered
      handlePidlDocRendered: PropTypes.func, // Triggered right after the Pidl form is rendered
      handleProcessingComplete: PropTypes.func, // Triggered if the rendering hits some error
    }).isRequired,
    isModern3ds: PropTypes.bool.isRequired,
    // modern 3ds popup element ID rendered by PIDL sdk, required when isModern3ds == true
    modern3dsPopup: PropTypes.string,
    permissions: PropTypes.shape({}),
  };

  static defaultProps: Partial<ScaChallengerProps> = {
    ...PropTypesDefaultGenericPidl,
    challengeWindowSize: '04', // Recommended default size
    permissions: {},
    modern3dsPopup: 'pidlddc-button-submitButton',
  };


  paymentSessionData: import('../types').PaymentSessionData;

  pidlFormId: string;

  pidlUtil: PidlUtil;

  constructor(props: ScaChallengerProps) {
    super(props);
    this.state = {
      pidlInstance: null,
      showModal: false,
    };

    this.pidlUtil = new PidlUtil(props);

    // https://osgwiki.com/wiki/Pidlsdk#PaymentSession_createPaymentSession_.28PaymentSessionData.29
    this.paymentSessionData = {
      piid: this.props.piData.CtpPiId,
      language: this.pidlUtil.pidlDocParameters.language,
      partner: this.pidlUtil.pidlDocParameters.partner,
      amount: Math.round(this.props.purchaseContext.amount * 100) / 100,
      currency: this.props.purchaseContext.currency,
      country: this.props.purchaseContext.country,
      challengeScenario: this.props.purchaseContext.challengeScenario,
      challengeWindowSize: this.props.challengeWindowSize,
    };

    this.pidlFormId = _.uniqueId('pidl-container-');
  }

  componentDidMount() {
    const pidlCallbacks = {

      successCallback: (response: PaymentSession) => this.successCallback(response),
      goHomeCallback: () => this.goHomeCallback(),
    };

    const supplementalCallbacks = {

      submit: (request: { data: { cvvToken?: string } }) => this.submitCallback(request),
    };

    const displayOptions = {
      containerId: this.pidlFormId,
      showPlaceholderInTextbox: true,
    };


    const errorHandler = (error: PidlError) => this.pidlInstanceErrorHandler(error);

    this.pidlUtil.getPidlInstance({
      piData: this.props.piData,
      securityAction: securityAction.PaymentInstrLoad,
      pidlCallbacks,

      docDisplayEventHandler: (eventName: PidlEventName, parameters?: PidlEventParameters) => this.docDisplayEventHandler(eventName, parameters),
      displayOptions,
      errorHandler,
      // To test in SI and Prod, can use 456 for success
      testHeader: this.props.testHeader,
      supplementalCallbacks,
    }).then((pidlInstance: PidlInstance) => this.setState({
      pidlInstance,
    })).catch(errorHandler);
  }


  pidlInstanceErrorHandler = (error: PidlError) => {
    this.setState({ showModal: false });
    switch (error.originalPidlErrorMessage) {
      case 'Failed':
      case 'TimedOut':
        this.pidlUtil.logger.fail(error.originalPidlErrorMessage);
        this.props.eventHandlers.handleScaChallengeFailure();
        break;
      default:
        this.pidlUtil.logger.fail(error.originalPidlErrorMessage);
        this.props.eventHandlers.handleTerminatingError(error);
        break;
    }
  };


  successCallback = (paymentSession: PaymentSession) => {
    this.setState({ showModal: false });
    const { challengeStatus, id } = paymentSession;

    switch (challengeStatus) {
      case 'Succeeded':
      case 'NotApplicable':
      case 'ByPassed':
        this.pidlUtil.logger.succeed(challengeStatus);
        this.props.eventHandlers.handleScaChallengeSuccess(id);
        break;
      default:
        this.pidlUtil.logger.fail(challengeStatus);
        this.props.eventHandlers.handleScaChallengeFailure();
        break;
    }
  };

  goHomeCallback = () => {
    this.setState({ showModal: false });
    this.pidlUtil.logger.userFail('CancelClicked');
    this.props.eventHandlers.handleGoHome();
  };


  submitCallback = (request: { data: { cvvToken?: string } }) => {
    const { cvvToken } = request.data;
    if (cvvToken) {
      this.pidlUtil.logger.succeed('CvvTokenAcquired');
      this.props.eventHandlers.handle3dsChallengeSuccess(cvvToken);
    } else {
      // The error callback may change
      this.pidlUtil.logger.fail('CvvTokenUnavailable');
      this.props.eventHandlers.handleScaChallengeFailure();
    }
  };


  docDisplayEventHandler = (eventName: PidlEventName, parameters?: PidlEventParameters) => {
    let error;
    switch (eventName) {
      case 'pageRendering':
        this.setState({ showModal: true });
        this.props.eventHandlers.handleProcessingStart();
        break;
      case 'pageRendered':
        this.pidlUtil.logger.succeed('renderPidl');
        if (this.paymentSessionData.country === 'IN' && this.props.isModern3ds) {
          document.getElementById(this.props.modern3dsPopup).onclick = () => {
            const popupWindow = IWindow.open('', 'm3dsPopup', 'width=800,height=1000');
            ReactDOM.render(
              <LoadingMessage i18n={this.props.i18n} />,
              popupWindow.document.documentElement
            );
            popupWindow.onunload = () => {
              this.pidlUtil.logger.succeed(`Popup closed from: ${popupWindow.location.href}`);
            };
            popupWindow.focus();
            this.props.eventHandlers.handlePidlDocRendered(popupWindow);
          };
        } else {
          this.props.eventHandlers.handlePidlDocRendered();
        }
        break;
      case 'error':
        error = parameters && parameters.error;
        this.pidlUtil.logger.validationFail('submit', error);
        this.props.eventHandlers.handleProcessingComplete();
        break;
      default:
        break;
    }
  };

  formatModern3dsInfoText() {
    if (this.props.isModern3ds) {
      return ElementHelper.renderTextWithBolds(
        this.props.i18n.getString(_TL_("When you click {0}, we'll send you to your bank's website to authorize this charge.")),
        [this.props.i18n.getString(_TL_('Next'))]
      );
    }
    return null;
  }

  render() {
    return (
      <div id="sca-challenger-container">
        <ScaChallengeStyles />
        <ScaChallengerPidlForm
          pidlInstance={this.state.pidlInstance}
          pidlUtil={this.pidlUtil}
          paymentSessionData={this.paymentSessionData}
          pidlFormId={this.pidlFormId}
          showModal={this.state.showModal}
          modalTitle={this.props.i18n.getString(_TL_('Authenticate your card'))}
          onHideModalCallbackHandler={() => this.goHomeCallback()}
          isModern3ds={this.props.isModern3ds}
          modern3dsInfoText={this.formatModern3dsInfoText()}
          piType={this.props.piData.PiType}
          permissions={this.props.permissions}
        />
      </div>
    );
  }
}
